import React, { ReactNode, useState } from 'react'

import * as DialogPrimitive from '@radix-ui/react-dialog'

import { ReactComponent as CaretRightIcon } from '@app/assets/Small/Outline/CaretRight.svg'
import { bemJoin } from '@app/lib/bem'
import { useAppRoot } from '@app/lib/context/AppRoot'
import { VisuallyHidden } from '@app/lib/display/VisuallyHidden/VisuallyHidden'

import { MenuItemContent } from './MenuItemContent'
import { DropdownItem, DropdownMenuSize } from './types'

const b = bemJoin('dropdown-menu')

export type DropdownMenuMobileProps = {
  items: DropdownItem[]
  trigger: ReactNode
  title: string
  size: DropdownMenuSize
  className?: string
}

const MobileItem = ({
  item,
  size,
  onClose
}: {
  item: DropdownItem
  size: DropdownMenuSize
  onClose: () => void
}) => {
  const [expanded, setExpanded] = useState(false)

  if (item.items?.length) {
    return (
      <div className={b('accordion', { expanded })}>
        <button
          type='button'
          className={b('row')}
          disabled={item.disabled}
          aria-expanded={expanded}
          onClick={() => setExpanded(!expanded)}
        >
          <MenuItemContent
            label={item.label}
            leading={item.leading}
            comment={item.comment}
            trailing={<CaretRightIcon className={b('chevron', { accordion: true, expanded })} />}
            size={size}
            selected={item.selected}
            disabled={item.disabled}
          />
        </button>
        {expanded && (
          <div className={b('accordion-body')}>
            <MobileItemList items={item.items} size={size} onClose={onClose} />
          </div>
        )}
      </div>
    )
  }

  return (
    <button
      type='button'
      className={b('row')}
      disabled={item.disabled}
      onClick={() => {
        item.onClick?.()
        onClose()
      }}
    >
      <MenuItemContent
        label={item.label}
        leading={item.leading}
        comment={item.comment}
        size={size}
        selected={item.selected}
        disabled={item.disabled}
      />
    </button>
  )
}

const MobileItemList = ({
  items,
  size,
  onClose
}: {
  items: DropdownItem[]
  size: DropdownMenuSize
  onClose: () => void
}) => {
  return (
    <>
      {items.map((item) => (
        <MobileItem key={item.key} item={item} size={size} onClose={onClose} />
      ))}
    </>
  )
}

export const DropdownMenuMobile = ({
  items,
  trigger,
  title,
  size,
  className
}: DropdownMenuMobileProps) => {
  const { ref: appRootRef } = useAppRoot()
  const [open, setOpen] = useState(false)

  return (
    <DialogPrimitive.Root open={open} onOpenChange={setOpen}>
      <DialogPrimitive.Trigger asChild className={className}>
        {trigger}
      </DialogPrimitive.Trigger>

      <DialogPrimitive.Portal container={appRootRef.current}>
        <DialogPrimitive.Overlay className={b('backdrop')}>
          <DialogPrimitive.Content className={b('panel')} aria-describedby={undefined}>
            <DialogPrimitive.Title asChild>
              <VisuallyHidden>{title}</VisuallyHidden>
            </DialogPrimitive.Title>
            <div className={b('panel-scroll')}>
              <MobileItemList items={items} size={size} onClose={() => setOpen(false)} />
            </div>
          </DialogPrimitive.Content>
        </DialogPrimitive.Overlay>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}
